import { useState, useEffect, useCallback } from 'react';
import { 
  Settings2, FolderOpen, Zap, AlertCircle, Loader2, RefreshCw, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import MainLayout from '@/components/layout/MainLayout';

const formatNumber = (num) => new Intl.NumberFormat('es-ES').format(num || 0);
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES', { 
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
  });
};

const LOG_LEVEL_COLORS = {
  info: 'text-blue-400 bg-blue-500/10',
  warning: 'text-amber-400 bg-amber-500/10',
  error: 'text-red-400 bg-red-500/10',
  success: 'text-green-400 bg-green-500/10'
};

const AdminSistema = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [systemStats, setSystemStats] = useState(null);
  const [systemLogs, setSystemLogs] = useState([]);
  const [cleaning, setCleaning] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, logsRes] = await Promise.all([
        api.get('/admin/system/stats'),
        api.get('/admin/system/logs?limit=50')
      ]);
      setSystemStats(statsRes.data);
      setSystemLogs(logsRes.data.logs || []);
    } catch (err) {
      toast.error('Error cargando datos del sistema');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCleanup = async () => {
    if (!window.confirm('¿Seguro que quieres limpiar los borradores antiguos (>30 días)?')) return;
    
    setCleaning(true);
    try {
      const res = await api.delete('/admin/system/cleanup');
      toast.success(`Limpieza completada: ${res.data.deleted || 0} elementos eliminados`);
      fetchData();
    } catch (err) {
      toast.error('Error en la limpieza');
    } finally {
      setCleaning(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-brand" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="admin-sistema-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-main flex items-center gap-3">
              <Settings2 className="w-7 h-7 text-brand" />
              Sistema
            </h1>
            <p className="text-text-muted mt-1">Estado técnico y logs del sistema</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCleanup} variant="outline" disabled={cleaning}>
              <Trash2 className={`w-4 h-4 mr-2 ${cleaning ? 'animate-pulse' : ''}`} />
              Limpiar drafts
            </Button>
            <Button onClick={fetchData} variant="outline" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface border border-border-subtle rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-muted">Total Funnels</span>
              <FolderOpen className="w-4 h-4 text-brand" />
            </div>
            <p className="text-2xl font-bold text-text-main">{formatNumber(systemStats?.total_funnels || 0)}</p>
            <p className="text-xs text-text-muted mt-1">En la plataforma</p>
          </div>
          
          <div className="bg-surface border border-border-subtle rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-muted">Assets Generados</span>
              <Zap className="w-4 h-4 text-purple-400" />
            </div>
            <p className="text-2xl font-bold text-text-main">{formatNumber(systemStats?.total_assets || 0)}</p>
            <p className="text-xs text-text-muted mt-1">Imágenes, textos, videos</p>
          </div>
          
          <div className="bg-surface border border-border-subtle rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-muted">Créditos Hoy</span>
              <Zap className="w-4 h-4 text-amber-400" />
            </div>
            <p className="text-2xl font-bold text-text-main">{formatNumber(systemStats?.credits_consumed_today || 0)}</p>
            <p className="text-xs text-amber-400 mt-1">Consumidos hoy</p>
          </div>
          
          <div className="bg-surface border border-border-subtle rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-muted">Errores 24h</span>
              <AlertCircle className="w-4 h-4 text-red-400" />
            </div>
            <p className="text-2xl font-bold text-text-main">{formatNumber(systemStats?.errors_24h || 0)}</p>
            <p className="text-xs text-red-400 mt-1">Últimas 24 horas</p>
          </div>
        </div>

        {/* Additional Stats */}
        {systemStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <span className="text-xs text-text-muted">Usuarios Activos (7d)</span>
              <p className="text-xl font-bold text-text-main mt-1">{formatNumber(systemStats.active_users_7d || 0)}</p>
            </div>
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <span className="text-xs text-text-muted">Funnels Completados</span>
              <p className="text-xl font-bold text-text-main mt-1">{formatNumber(systemStats.completed_funnels || 0)}</p>
            </div>
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <span className="text-xs text-text-muted">Créditos Este Mes</span>
              <p className="text-xl font-bold text-text-main mt-1">{formatNumber(systemStats.credits_consumed_month || 0)}</p>
            </div>
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <span className="text-xs text-text-muted">Almacenamiento</span>
              <p className="text-xl font-bold text-text-main mt-1">{systemStats.storage_used || '0 MB'}</p>
            </div>
          </div>
        )}

        {/* System Logs */}
        <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border-subtle flex items-center justify-between">
            <h3 className="font-medium text-text-main">Logs del Sistema</h3>
            <span className="text-xs text-text-muted">Últimos 50 registros</span>
          </div>
          <div className="max-h-[400px] overflow-auto">
            {systemLogs.length === 0 ? (
              <p className="p-8 text-center text-text-muted">No hay logs recientes</p>
            ) : (
              systemLogs.map((log) => (
                <div 
                  key={log.id || `log-${log.created_at}-${log.message?.slice(0,20)}`} 
                  className="flex items-start gap-3 p-3 border-b border-border-subtle last:border-0 hover:bg-surface-hover"
                >
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${LOG_LEVEL_COLORS[log.level] || LOG_LEVEL_COLORS.info}`}>
                    {(log.level || 'info').toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-main">{log.message}</p>
                    {log.details && (
                      <p className="text-xs text-text-muted mt-1 truncate">{log.details}</p>
                    )}
                  </div>
                  <span className="text-xs text-text-muted whitespace-nowrap">
                    {formatDate(log.created_at)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminSistema;
